import clsx from "clsx";
import React, { useId } from "react";
import { forwardRef } from "react";
import { FieldLabel } from "~/components/Forms";
import { Switch } from "@headlessui/react";

export const ToggleIconField = forwardRef<HTMLDivElement, Omit<React.ComponentPropsWithoutRef<"input">, "id" | "type" | "onChange"> & {
    label?: string;
    labelClassName?: string;
    setValueAction: (value: boolean) => void;
    required?: boolean;
    error?: any;
    note?: string;
    noteClassName?: string;
}>(
    (
        {
            className,
            label,
            labelClassName,
            checked,
            value,
            defaultValue,
            setValueAction,
            required,
            error,
            note,
            noteClassName,
            ...props
        },
        ref
    ) => {
        const id = useId();

        const normalizeBoolean = (input: unknown): boolean | undefined => {
            if (typeof input === "boolean") return input;
            if (typeof input === "number") return input !== 0;
            if (typeof input === "string") {
                const normalized = input.trim().toLowerCase();
                if (["true", "1", "on", "yes"].includes(normalized)) return true;
                if (["false", "0", "off", "no", ""].includes(normalized)) return false;
            }
            return undefined;
        };

        const isOn =
            normalizeBoolean(checked) ??
            normalizeBoolean(value) ??
            normalizeBoolean(defaultValue) ??
            false;

        return (
            <div ref={ref} className={className}>
                <div className="flex flex-row gap-4 items-start justify-center">
                    <div className="inline-flex">
                        {/* keep hidden input for form posts; ensure string value */}
                        <input type="hidden" {...props} value={isOn ? "true" : "false"} />
                        <Switch
                            id={id}
                            checked={isOn}
                            onChange={(checked) => setValueAction(checked)}
                            className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-zinc-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-grass-500 focus:ring-offset-2 data-checked:bg-grass-400"
                        >
              <span className="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-checked:translate-x-5">
                <span
                    aria-hidden="true"
                    className="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in group-data-checked:opacity-0 group-data-checked:duration-100 group-data-checked:ease-out"
                >
                  <svg fill="none" viewBox="0 0 12 12" className="h-3 w-3 text-gray-400">
                    <path
                        d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span
                    aria-hidden="true"
                    className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-checked:opacity-100 group-data-checked:duration-200 group-data-checked:ease-in">
                  <svg fill="currentColor" viewBox="0 0 12 12" className="h-3 w-3 text-grass-700">
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              </span>
                        </Switch>
                    </div>

                    <div className="flex grow items-center justify-left">
                        {label && (
                            <FieldLabel
                                id={id}
                                required={!!required}
                                className={clsx(labelClassName, "text-left leading-tight! pb-0!")}
                            >
                                {label}
                            </FieldLabel>
                        )}
                    </div>
                </div>

                {note && <div className={clsx("field-note", noteClassName)}>{note}</div>}
                {error && <div className="field-error-text">{error}</div>}

            </div>
        );
    }
);
